import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormikProvider, useFormik } from 'formik';
import { Button, Input, ModalFactory } from 'components';
import Form from 'screens/Error/Form';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { LaborReview as LaborReviewActionTypes, updateLaborRequest } from 'store/LaborReview';
import { IStore } from 'store/types';
import { ILabor } from 'types';
import { OverrideReasonCallout } from './OverrideReasonCallout';
import { buildShiftValidationSchema, haveSchedulesChanged, IShiftFormValues, newFormSchedule, ShiftFieldArray, toFormSchedule, toLaborSchedule } from './ShiftForm';
import { ModalButtonRow, ModalSubtitle, RequiredField, ShiftFormLayout } from './styles';
import { formatBusinessDate } from './utils';

interface IEditShiftModalProps {
  siteId: string;
  date: string;
  locationId: string;
  timezone: string;
  businessDayStart: string;
  employeeId: string;
  employeeName?: string;
  labor: ILabor;
  jobTitles: string[];
  onClose: () => void;
}

const validationSchema = buildShiftValidationSchema('Please provide a reason for this change');

export const EditShiftModal: React.FC<IEditShiftModalProps> = ({ siteId, date, locationId, timezone, businessDayStart, employeeId, employeeName, labor, jobTitles, onClose }) => {
  const dispatch = useDispatch();
  const isUpdating = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, LaborReviewActionTypes.UPDATE_LABOR_REQUEST));

  const formik = useFormik<IShiftFormValues>({
    initialValues: {
      schedules: labor.schedules.length ? labor.schedules.map((schedule) => toFormSchedule(schedule, timezone)) : [newFormSchedule()],
      reason: '',
    },
    onSubmit: (formValues) => {
      const schedules = formValues.schedules.map((schedule) => toLaborSchedule({ schedule, date, timezone, businessDayStart }));

      dispatch(updateLaborRequest(siteId, date, locationId, employeeId, { schedules, version: labor.version, reason: formValues.reason.trim() }, onClose));
    },
    // Flags the times Legion sent without a clock out or a break end from the start, so they're easy to spot.
    initialTouched: {
      schedules: labor.schedules.map((schedule) => ({ end: !schedule.end, breaks: schedule.breaks.map((laborBreak) => ({ end: !laborBreak.end })) })),
    },
    validateOnMount: true,
    validateOnBlur: true,
    validationSchema,
  });

  const { handleSubmit, initialValues, setFieldValue, values } = formik;

  const hasScheduleChanges = haveSchedulesChanged(initialValues.schedules, values.schedules);
  // An override's reason can be amended on its own, keeping the shifts as they are.
  const trimmedReason = values.reason.trim();
  const hasReasonChange = Boolean(labor.overridden) && Boolean(trimmedReason) && trimmedReason !== labor.overridden.reason;

  const reasonError = formik.touched.reason && formik.errors.reason ? formik.errors.reason : '';

  return (
    <ModalFactory title={`Edit labor — ${employeeName || `#${employeeId}`}`} onClose={onClose} isActive maxWidth={566} closeOnOutSideClick={false}>
      <ModalSubtitle>Changes replace the data Legion sent for {formatBusinessDate(date)}.</ModalSubtitle>

      <OverrideReasonCallout overridden={labor.overridden} label="This labor already has a manual override" />

      <FormikProvider value={formik}>
        <Form onSubmit={handleSubmit} dataCy="edit-shift-form">
          <ShiftFormLayout>
            <ShiftFieldArray schedules={values.schedules} jobTitles={jobTitles} setFieldValue={setFieldValue} />

            <RequiredField label="Reason for change" message={reasonError} hasError={Boolean(reasonError)}>
              <Input placeholder="Explain why this shift is being changed" data-cy="override-reason" {...formik.getFieldProps('reason')} />
            </RequiredField>
          </ShiftFormLayout>

          <ModalButtonRow>
            <Button appearance="white" label="Cancel" onClick={onClose} disabled={isUpdating} />
            <Button type="submit" label="Save changes" isProcessing={isUpdating} disabled={isUpdating || !(hasScheduleChanges || hasReasonChange)} />
          </ModalButtonRow>
        </Form>
      </FormikProvider>
    </ModalFactory>
  );
};
